import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { API_BASE } from "../config";

function formatDateToDDMMYYYY(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date)) return "";
  const day = ("0" + date.getDate()).slice(-2);
  const month = ("0" + (date.getMonth() + 1)).slice(-2);
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

const UsgReceiptPrint = ({ show, data, companyDetails, onClose }) => {
  const [showHeader, setShowHeader] = useState(true);
  const [usgHeader, setUsgHeader] = useState(null);

  useEffect(() => {
    if (!show) return;
    const token = sessionStorage.getItem("token");
    const headers = {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
    fetch(`${API_BASE}/USGReportHeader`, { method: "GET", headers })
      .then((res) => (res.ok ? res.json() : null))
      .then((result) => {
        if (result && result.hospitalName) setUsgHeader(result);
      })
      .catch((err) => console.error("Error fetching USG Header:", err));
  }, [show]);

  if (!show || !data) return null;

  const handlePrint = () => window.print();
  const handleToggleHeader = () => setShowHeader((v) => !v);

  const createMarkup = (htmlContent) => {
    if (!htmlContent) return { __html: "" };
    let decoded = htmlContent;
    if (decoded.includes("&lt;") || decoded.includes("&gt;")) {
      const txt = document.createElement("textarea");
      txt.innerHTML = decoded;
      decoded = txt.value;
    }
    return { __html: decoded };
  };

  const patientName   = data.patientName || data.name || data.PatientName || "";
  const uhid          = data.uhid_No || data.UHID_No || data.uhiD_No || "-";
  const patientMobile = data.mobileNo || data.MobileNo || "";
  const ageGender     = `${data.age || "-"}/${data.gender || "-"}`;
  const doctorName    = data.doctorName || data.consultantName || ".......................";
  const reportDateFormatted = formatDateToDDMMYYYY(data.reportDate || new Date());
  const receiptNo     = data.hid_SrNo || data.id || "-";
  const sourceType    = data.sourceType || "OPD";

  const getImageUrl = (path) => {
    if (!path) return "";
    const base = API_BASE.replace(/\/api\/?$/, "");
    return `${base}/${path.replace(/^\//, "")}`;
  };

  const hName    = usgHeader?.hospitalName || companyDetails?.company_Name || "HOSPITAL NAME";
  const subName  = usgHeader?.subName || "";
  const regNo    = usgHeader?.regNo || companyDetails?.reg_No || "";
  const hAddress = `${usgHeader?.address || companyDetails?.address || ""}${usgHeader?.landmark ? ", " + usgHeader.landmark : ""}`;
  const mob1     = usgHeader?.contact1 || companyDetails?.contact1 || "";
  const mob2     = usgHeader?.contact2 || companyDetails?.contact2 || "";
  const hEmail   = usgHeader?.email || companyDetails?.email_ID || "";

  const mainLogoPath     = usgHeader?.logo || companyDetails?.logo || companyDetails?.Logo;
  const ayushmanLogoPath = usgHeader?.ayushmanLogo || companyDetails?.ayushmanLogo || companyDetails?.AyushmanLogo;
  const isAyushmanActive = usgHeader?.isAyushmanActive ?? companyDetails?.isAyushmanActive ?? false;

  // ─────────────────────────────────────────────────────────────
  // HOSPITAL HEADER BLOCK (repeats on every printed page via <thead>)
  // ─────────────────────────────────────────────────────────────
  const HospitalHeaderBlock = () => (
    showHeader ? (
      <div className="usg-top-header">
        <div className="usg-logo-col left">
          {mainLogoPath && <img src={getImageUrl(mainLogoPath)} alt="Logo" />}
        </div>
        <div className="usg-center-col">
          <h1 className="usg-title">{hName}</h1>
          {subName && <h2 className="usg-sub-title">{subName}</h2>}
          <p className="usg-address-line">
            {regNo && <span><strong>Reg. No:</strong> {regNo} &nbsp;|&nbsp; </span>}
            {hAddress}
          </p>
          <p className="usg-contacts">
            <strong>Ph:</strong> {mob1}{mob2 ? ` / ${mob2}` : ""}
            {hEmail && <>&nbsp;|&nbsp;<strong>Email:</strong> {hEmail}</>}
          </p>
        </div>
        <div className="usg-logo-col right">
          {isAyushmanActive && ayushmanLogoPath && <img src={getImageUrl(ayushmanLogoPath)} alt="Ayushman" />}
        </div>
      </div>
    ) : (
      <div className="usg-letterhead-spacer" />
    )
  );

  // ─────────────────────────────────────────────────────────────
  // PATIENT DETAILS BLOCK (part of repeating header)
  // ─────────────────────────────────────────────────────────────
  const PatientDetailsBlock = () => (
    <>
      <div className="marg-title-strip">DIAGNOSTIC REPORT</div>
      <div className="marg-patient-box">
        <div className="marg-patient-left">
          <table>
            <tbody>
              <tr>
                <td style={{ width: "32%" }}><strong>Patient Name</strong></td>
                <td style={{ width: "4%" }}>:</td>
                <td><strong style={{ fontSize: "13px", textTransform: "uppercase" }}>{patientName}</strong></td>
              </tr>
              <tr>
                <td><strong>UHID / Mobile</strong></td>
                <td>:</td>
                <td>{uhid}&nbsp;|&nbsp;{patientMobile}</td>
              </tr>
              <tr>
                <td><strong>Address</strong></td>
                <td>:</td>
                <td>{data.address || "-"}</td>
              </tr>
              <tr>
                <td><strong>Referred By</strong></td>
                <td>:</td>
                <td><strong>Dr. {doctorName}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="marg-patient-right">
          <table>
            <tbody>
              <tr>
                <td className="pat-r-label"><strong>Report No.</strong></td>
                <td className="pat-r-colon">:</td>
                <td className="pat-r-value"><strong>{receiptNo}</strong></td>
              </tr>
              <tr>
                <td className="pat-r-label"><strong>Date</strong></td>
                <td className="pat-r-colon">:</td>
                <td className="pat-r-value">{reportDateFormatted}</td>
              </tr>
              <tr>
                <td className="pat-r-label"><strong>Age / Sex</strong></td>
                <td className="pat-r-colon">:</td>
                <td className="pat-r-value">{ageGender}</td>
              </tr>
              <tr>
                <td className="pat-r-label"><strong>Source</strong></td>
                <td className="pat-r-colon">:</td>
                <td className="pat-r-value"><strong>{sourceType}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  // ─────────────────────────────────────────────────────────────
  // FOOTER / SIGNATURE BLOCK (repeats on every printed page via <tfoot>)
  // ─────────────────────────────────────────────────────────────
  const FooterBlock = () => (
    <div className="marg-sig-row">
      <div className="marg-sig-block">
        <div className="marg-sig-line">AUTHORIZED SIGNATORY</div>
        <div className="marg-sig-sub">(Consultant Radiologist / Pathologist)</div>
      </div>
    </div>
  );

  return createPortal(
    <div className="usg-print-portal-root">
      <style>{`
        /* ═══════════════════════════════════════════════════════════
           SCREEN VIEW: Modal Background
        ═══════════════════════════════════════════════════════════ */
        .usg-modal-backdrop {
          position: fixed;
          top: 0; left: 0;
          width: 100%; height: 100%;
          background: rgba(0,0,0,0.65);
          display: flex;
          justify-content: center;
          align-items: flex-start;
          z-index: 10500;
          padding: 20px;
          overflow-y: auto;
          box-sizing: border-box;
        }
        .usg-modal-wrapper {
          background: #fff;
          border-radius: 8px;
          width: 100%;
          max-width: 820px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.3);
          overflow: hidden;
        }
        .usg-action-bar {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding: 10px 16px;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
        }

        /* ═══════════════════════════════════════════════════════════
           BASE PRINT SECTION (screen + print shared)
        ═══════════════════════════════════════════════════════════ */
        #marg-print-section {
          font-family: Arial, Helvetica, sans-serif;
          color: #000;
          background: #fff;
          font-size: 12px;
          box-sizing: border-box;
          width: 100%;
          margin: 0 auto;
        }
        #marg-print-section * { box-sizing: border-box; }

        /* ───────────────────────────────────────────────────────────
           THE MASTER TABLE — this is what makes multi-page work.
           thead repeats on every page top, tfoot on every page bottom.
        ─────────────────────────────────────────────────────────── */
        .print-master-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
        }
        .print-master-table > thead { display: table-header-group; }
        .print-master-table > tfoot { display: table-footer-group; }
        .print-master-table > tbody { display: table-row-group; }
        .print-master-table td,
        .print-master-table th { padding: 0; }

        /* Screen preview border around the whole "page" */
        .screen-page-preview {
          border: 2px solid #000;
          position: relative;
          margin: 10px auto;
        }

        /* ═══════════════════════════════════════════════════════════
           HOSPITAL HEADER
        ═══════════════════════════════════════════════════════════ */
        .usg-top-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 14px;
          border-bottom: 2px solid #000;
          background: #fff;
          margin: 0;
        }
        .usg-logo-col { width: 20%; display: flex; align-items: center; }
        .usg-logo-col.left  { justify-content: flex-start; }
        .usg-logo-col.right { justify-content: flex-end; }
        .usg-logo-col img { max-width: 100%; max-height: 95px; object-fit: contain; display: block; }
        .usg-logo-col.right img {
          max-height: 95px;
          transform: scale(1.2);
        }

        .usg-center-col { width: 60%; text-align: center; padding: 0 8px; }
        .usg-title { margin: 0; padding: 0; font-weight: 900; font-size: 23px; color: red; text-transform: uppercase; letter-spacing: 0.8px; line-height: 1.15; }
        .usg-sub-title { margin: 3px 0 0 0; padding: 0; font-weight: bold; font-size: 13.5px; color: #333; text-transform: uppercase; line-height: 1.2; }
        .usg-address-line { margin: 4px 0 0 0; font-size: 11px; color: #111; font-weight: 500; line-height: 1.4; }
        .usg-contacts { margin: 3px 0 0 0; font-size: 11px; color: #222; font-weight: 600; }
        .usg-letterhead-spacer { height: 120px; }

        /* ═══════════════════════════════════════════════════════════
           TITLE STRIP & PATIENT DETAILS
        ═══════════════════════════════════════════════════════════ */
        .marg-title-strip {
          background: #e0e0e0;
          border-bottom: 2px solid #000;
          padding: 4px 8px;
          font-weight: 900;
          font-size: 13px;
          letter-spacing: 1px;
          text-align: center;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        .marg-patient-box { display: flex; border-bottom: 2px solid #000; }
        .marg-patient-left { width: 58%; border-right: 2px solid #000; padding: 7px 11px; }
        .marg-patient-right { width: 42%; padding: 7px 11px; }
        .marg-patient-box table { width: 100%; font-size: 12px; line-height: 1.65; border-collapse: collapse; }
        .marg-patient-box table td { padding: 1px 0; vertical-align: top; }

        .pat-r-label { text-align: right; white-space: nowrap; width: 50%; padding-right: 2px; }
        .pat-r-colon { text-align: center; width: 8%; }
        .pat-r-value { text-align: right; padding-left: 4px; }

        /* ═══════════════════════════════════════════════════════════
           REPORT CONTENT
        ═══════════════════════════════════════════════════════════ */
        .usg-report-container { padding: 14px 16px 8px 16px; }
        .usg-test-heading {
          font-size: 14px;
          font-weight: bold;
          text-align: center;
          text-transform: uppercase;
          text-decoration: underline;
          margin: 0 0 14px 0;
          padding: 0;
          page-break-after: avoid;
        }
        .usg-report-html {
          font-size: 12px;
          line-height: 1.6;
          color: #000;
          text-align: justify;
          word-wrap: break-word;
        }
        .usg-report-html p { margin: 0 0 6px 0; }
        .usg-report-html table { border-collapse: collapse; width: 100%; margin-bottom: 10px; }
        .usg-report-html table td,
        .usg-report-html table th { border: 1px solid #bbb; padding: 4px 6px; font-size: 12px; }
        .usg-report-html img { max-width: 100%; height: auto; }

        .usg-remarks-block {
          margin-top: 16px;
          padding-top: 7px;
          border-top: 1px dashed #aaa;
          font-size: 12px;
        }

        /* ═══════════════════════════════════════════════════════════
           SIGNATURE BLOCK
        ═══════════════════════════════════════════════════════════ */
        .marg-sig-row {
          display: flex;
          justify-content: flex-end;
          padding: 22px 20px 10px 20px;
        }
        .marg-sig-block { text-align: center; min-width: 210px; }
        .marg-sig-line { border-top: 1.5px solid #000; padding-top: 5px; font-weight: 900; font-size: 11.5px; letter-spacing: 0.5px; text-transform: uppercase; }
        .marg-sig-sub { font-size: 10.5px; color: #444; margin-top: 3px; }

        /* Screen: side borders on the header/footer cells so the preview
           looks like a bordered page even before printing */
        .screen-page-preview .print-master-table > thead .cell-header,
        .screen-page-preview .print-master-table > tbody .cell-body,
        .screen-page-preview .print-master-table > tfoot .cell-footer {
          border-left: 0;
          border-right: 0;
        }

        /* ═══════════════════════════════════════════════════════════
           🔥 PRINT-ONLY STYLES
        ═══════════════════════════════════════════════════════════ */
        @media print {
          @page {
            size: A4 portrait;
            margin: 8mm 6mm;
          }

          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* Hide every other top-level sibling of body (the whole app) using
             display:none so it takes NO layout space. The portal root stays
             visible and starts at the very top of the page. */
          body > *:not(.usg-print-portal-root) { display: none !important; }

          /* Strip screen-only wrappers/styling */
          .usg-modal-backdrop {
            position: static !important;
            background: transparent !important;
            padding: 0 !important;
            overflow: visible !important;
            display: block !important;
          }
          .usg-modal-wrapper {
            box-shadow: none !important;
            border-radius: 0 !important;
            max-width: 100% !important;
            width: 100% !important;
            overflow: visible !important;
          }
          .screen-page-preview {
            border: none !important;
            margin: 0 !important;
            position: static !important;
          }

          /* static (NOT absolute) — absolute positioning prevents the table from
             paginating, so multi-page content was being clipped to page 1.
             With static flow, thead/tfoot repeat and tbody flows to next pages. */
          #marg-print-section {
            position: static;
            width: 100%;
            margin: 0 !important;
            padding: 0 !important;
            background: #fff;
          }

          /* The master table drives pagination.
             thead -> header on every page
             tfoot -> footer on every page
             tbody -> content that flows & breaks naturally */
          .print-master-table {
            width: 100%;
          }
          .print-master-table > thead { display: table-header-group; }
          .print-master-table > tfoot { display: table-footer-group; }
          .print-master-table > tbody { display: table-row-group; }

          /* Outer page border drawn on the cells so it surrounds
             header, content and footer on every page */
          .cell-header {
            border-left: 2px solid #000;
            border-right: 2px solid #000;
            border-top: 2px solid #000;
          }
          .cell-body {
            border-left: 2px solid #000;
            border-right: 2px solid #000;
          }
          .cell-footer {
            border-left: 2px solid #000;
            border-right: 2px solid #000;
            border-bottom: 2px solid #000;
          }

          /* Page-break behaviour for the flowing content */
          .usg-report-container {
            padding: 14px 16px 8px 16px;
            page-break-inside: auto;
          }
          .usg-report-html {
            page-break-inside: auto;
            orphans: 3;
            widows: 3;
          }
          .usg-report-html p { page-break-inside: avoid; }
          .usg-report-html table { page-break-inside: auto; }
          .usg-report-html tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          .usg-test-heading { page-break-after: avoid; }
          .usg-remarks-block { page-break-inside: avoid; }

          /* Color preservation */
          .marg-title-strip,
          .usg-title {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .no-print { display: none !important; }
        }
      `}</style>

      {/* ═══════════════════════════════════════════════════════════
          SCREEN MODAL
      ═══════════════════════════════════════════════════════════ */}
      <div className="usg-modal-backdrop">
        <div className="usg-modal-wrapper">

          <div id="marg-print-section" className={!showHeader ? "header-hidden" : ""}>
            <div className="screen-page-preview">

              {/* ───────────────────────────────────────────────
                  MASTER TABLE: header(thead) + content(tbody) + footer(tfoot)
                  Browser natively repeats thead/tfoot on each printed page
                  and breaks tbody content across pages.
              ─────────────────────────────────────────────── */}
              <table className="print-master-table">

                {/* REPEATING HEADER */}
                <thead>
                  <tr>
                    <td className="cell-header">
                      <HospitalHeaderBlock />
                      <PatientDetailsBlock />
                    </td>
                  </tr>
                </thead>

                {/* REPEATING FOOTER (tfoot must come before tbody in HTML) */}
                <tfoot>
                  <tr>
                    <td className="cell-footer">
                      <FooterBlock />
                    </td>
                  </tr>
                </tfoot>

                {/* FLOWING CONTENT */}
                <tbody>
                  <tr>
                    <td className="cell-body">
                      <div className="usg-report-container">
                        <p className="usg-test-heading">{data.testName || "USG Report"}</p>
                        <div
                          className="usg-report-html"
                          dangerouslySetInnerHTML={createMarkup(data.testContent)}
                        />
                        {data.remarks && (
                          <div className="usg-remarks-block">
                            <strong>Remarks: </strong>{data.remarks}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                </tbody>

              </table>

            </div>
          </div>
          {/* END #marg-print-section */}

          {/* ── Action Buttons (screen only) ── */}
          <div className="usg-action-bar no-print">
            <button
              className={`btn btn-sm ${showHeader ? "btn-outline-secondary" : "btn-warning"} fw-medium px-3`}
              onClick={handleToggleHeader}
            >
              <i className={`bi ${showHeader ? "bi-eye-slash" : "bi-eye"} me-2`}></i>
              {showHeader ? "Use Letterhead (Hide Header)" : "Show Header"}
            </button>
            <button className="btn btn-secondary btn-sm px-4" onClick={onClose}>
              Close
            </button>
            <button
              className="btn btn-primary btn-sm px-5 fw-bold"
              onClick={handlePrint}
              style={{
                background: "var(--theme-color, #0088cc)",
                borderColor: "var(--theme-color, #0088cc)",
              }}
            >
              <i className="bi bi-printer me-2"></i> Print Report
            </button>
          </div>

        </div>
      </div>
    </div>,
    document.body
  );
};

export default UsgReceiptPrint;